import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Images, ScanFace, LayoutDashboard, ArrowRight, Loader2, KeyRound, Copy, Sparkles } from 'lucide-react';
import { API } from '../lib/constants';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { LoadingScreen } from '../components/LoadingScreen';
import { copyText } from '../lib/clipboard';

const DemoCard = ({ icon: Icon, step, title, body, children, tone = 'card-brutal', testId }) => (
  <div className={`${tone} p-7 flex flex-col fade-up`} data-testid={testId}>
    <div className="flex items-center justify-between mb-5">
      <span className="w-11 h-11 bg-cyan text-ink flex items-center justify-center shadow-sm"><Icon className="w-5 h-5" /></span>
      <span className="font-mono text-xs text-slate-500">{step}</span>
    </div>
    <h2 className="h-display text-2xl">{title}</h2>
    <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">{body}</p>
    <div className="mt-6 space-y-3">{children}</div>
  </div>
);

const DemoPage = () => {
  const navigate = useNavigate();
  const { login, account, logout } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${API}/public/demo`).then((r) => setCfg(r.data)).catch(() => toast.error('Demo unavailable right now')); }, []);

  const demoLogin = async () => {
    setBusy(true);
    try {
      if (account) await logout();
      await login(cfg.login.email, cfg.login.password);
      toast.success('Signed in to the demo organiser dashboard');
      navigate('/admin');
    } catch {
      toast.error('Demo login failed');
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <LoadingScreen label="Loading demo…" />;
  const slug = cfg.tenant?.slug;

  return (
    <div className="min-h-screen bg-ink grid-bg relative noise" data-testid="demo-page">
      <header className="glass-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo testId="demo-logo" />
          <div className="flex items-center gap-3">
            <Link to="/login" className="btn-ghost !py-2 hidden sm:inline-flex" data-testid="demo-login-link">Client login</Link>
            <Link to="/signup" className="btn-primary !py-2" data-testid="demo-signup-btn">Create your event <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-14 relative z-10">
        <div className="max-w-2xl mb-12 fade-up">
          <span className="badge-lime mb-5 inline-flex"><Sparkles className="w-3.5 h-3.5" /> Live demo · real photos, real matching</span>
          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">Try Spotfie as a guest, then as the organiser.</h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
            This is a real event workspace — <span className="text-white font-semibold">{cfg.tenant?.name}</span> — with {cfg.photo_count} photographer uploads already matched. Nothing here is a mock-up.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <DemoCard icon={Images} step="01" title="Browse a guest's album" body={`See exactly what a guest receives: ${cfg.guest_name}'s private album with only the photos she appears in, plus download and delete-my-data controls.`} tone="card-brutal-lime" testId="demo-card-album">
            <Link to={`/t/${slug}/gallery/${cfg.gallery_id}`} className="btn-lime w-full" data-testid="demo-open-album-btn">Open Demo Album <ArrowRight className="w-4 h-4" /></Link>
          </DemoCard>

          <DemoCard icon={ScanFace} step="02" title="Register as a guest" body="Walk through the branded portal: details → one selfie → your own QR and album link. Your selfie is never stored — you can delete your data right after." testId="demo-card-portal">
            <Link to={`/t/${slug}`} className="btn-primary w-full" data-testid="demo-open-portal-btn">Open the guest portal <ArrowRight className="w-4 h-4" /></Link>
          </DemoCard>

          <DemoCard icon={LayoutDashboard} step="03" title="Run the organiser dashboard" body="Upload photos, hand photographers upload links, watch auto-matching, tweak branding, and see the privacy controls — all on the Pro Event Pass." tone="card-brutal-white" testId="demo-card-dashboard">
            <button onClick={demoLogin} disabled={busy} className="btn-secondary w-full" data-testid="demo-dashboard-login-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} {busy ? 'Signing in…' : 'One-click organiser login'}
            </button>
            <div className="bg-ink border-2 border-line p-3 font-mono text-[11px] text-slate-400 space-y-1" data-testid="demo-credentials">
              <div className="flex items-center justify-between gap-2"><span>{cfg.login.email}</span><button onClick={async () => (await copyText(cfg.login.email)) && toast.success('Copied')} className="text-slate-500 hover:text-cyan"><Copy className="w-3 h-3" /></button></div>
              <div className="flex items-center justify-between gap-2"><span>{cfg.login.password}</span><button onClick={async () => (await copyText(cfg.login.password)) && toast.success('Copied')} className="text-slate-500 hover:text-cyan"><Copy className="w-3 h-3" /></button></div>
            </div>
          </DemoCard>
        </div>

        <div className="mt-12 card p-7 flex flex-col md:flex-row md:items-center justify-between gap-6 fade-up fade-up-delay-2" data-testid="demo-cta">
          <div>
            <p className="eyebrow mb-2">Ready for your own event?</p>
            <p className="h-display text-2xl">Create a free workspace in under a minute.</p>
            <p className="text-sm text-slate-400 mt-1">Your first event is free. The Pro Event Pass is a one-time ₹4,999 when you want it fully white-label.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link to="/signup" className="btn-primary" data-testid="demo-cta-signup">Create your event <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/" className="btn-ghost !py-3" data-testid="demo-back-home">Back to site</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DemoPage;
